import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";

import {
  buildSubmitData,
  convertBusinessHours,
  defaultTime,
  is24Hours,
  weekdays,
} from "../function/store";

import { BizTimeFormContainer, FormContainer, SettingContent } from "../styled/setting";

import Info from "./common/info";
import Switch from "./common/form/switch";
import SelectOptions from "./common/form/selectOptions";
import TimeInput from "./common/form/timeInput";
import CompanyPreview from "./company/companyPreview";

const BizTime = ({ company, setActive, selectCompany, openModal, closeModal, getCompany }) => {
  const [timeData, setTimeData] = useState(defaultTime);
  const message =
    Array.isArray(company.businessHours) && company.businessHours.length > 0 ? "수정" : "등록";

  useEffect(() => {
    setTimeData(company?.businessHours ? convertBusinessHours(company.businessHours) : defaultTime);
  }, [company]);

  const closeForm = () => {
    setActive("menuBoard");
  };

  // 시간 문자열 분(minute) 단위 숫자로 변환
  const timeToMinutes = (time) => {
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
  };

  // 개별 시간 항목 유효성 검사
  const validateTimeItem = (item, label = "") => {
    const isValidTime = (time) => /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);

    const { isOpen, openTime, closeTime, breakStartTime, breakEndTime, showBreak } = item;

    if (isOpen === false) {
      return true;
    }

    if (!isValidTime(openTime) || !isValidTime(closeTime)) {
      toast.error(`${label} 시작/종료 시간이 잘못됐습니다.`);
      return false;
    }

    if (!is24Hours(item) && timeToMinutes(openTime) >= timeToMinutes(closeTime)) {
      toast.error(`${label} 시작 시간은 종료 시간보다 빨라야 합니다.`);
      return false;
    }

    if (showBreak && !is24Hours(item)) {
      if (!breakStartTime) {
        toast.error(`${label} 휴게시간 시작 시간을 입력해주세요.`);
        return false;
      }

      if (!breakEndTime) {
        toast.error(`${label} 휴게시간 종료 시간을 입력해주세요.`);
        return false;
      }

      if (!isValidTime(breakStartTime) || !isValidTime(breakEndTime)) {
        toast.error(`${label} 휴게시간 형식을 확인해주세요.`);
        return false;
      }

      if (timeToMinutes(breakStartTime) >= timeToMinutes(breakEndTime)) {
        toast.error(`${label} 휴게 시작 시간은 종료 시간보다 빨라야 합니다.`);
        return false;
      }
    }

    return true;
  };

  // 요일별 설정 중 최소 하나는 휴무여야 유효
  const hasValidHoliday = () => {
    return timeData.openTimeData.byDay.some((item) => item.isOpen === false);
  };

  // 전체 유효성 검사
  const validationCheck = () => {
    const { hourType, hasHoliday } = timeData.openSetting;

    if (hasHoliday && !hasValidHoliday()) {
      toast.error("휴무일을 1개 이상 지정해주세요.");
      return false;
    }

    const { openTimeData } = timeData;

    if (hourType === "same") {
      return validateTimeItem(openTimeData.same);
    }

    if (hourType === "weekday-weekend") {
      return (
        validateTimeItem(openTimeData.weekday, "평일") &&
        validateTimeItem(openTimeData.weekend, "주말")
      );
    }

    if (hourType === "by-day") {
      return openTimeData.byDay.every((item) => validateTimeItem(item, weekdays[item.weekday]));
    }

    return false;
  };

  // 영업시간 서버 저장
  const handleSave = () => {
    if (!validationCheck()) return;

    openModal({
      type: "confirmation",
      title: `휴무일·영업시간 ${message}`,
      message: [`변경하신 휴무일·영업시간을 ${message}하시겠습니까?`],
      handleConfirm: updateCompany,
    });
  };

  const updateCompany = async () => {
    try {
      const url = "/erp/ss/company/hours";

      const input = buildSubmitData(timeData.openTimeData, timeData.openSetting.hourType);
      const body = {
        cmpnyNo: selectCompany.value,
        input,
      };

      const res = await axios.post(url, body);
      if (res.status === 200) {
        toast.success(`휴무일·영업시간이 성공적으로 ${message}되었습니다.`);
        setActive("menuBoard");
        getCompany(selectCompany);
      }
    } catch (error) {
      console.log(error);
    } finally {
      closeModal();
    }
  };

  const changeSwitch = (key, value) => {
    setTimeData((prev) => ({
      ...prev,
      openSetting: {
        ...prev.openSetting,
        [key]: value,
      },
    }));
  };

  const handleChange = (type) => (updatedValue) => {
    setTimeData((prev) => ({
      ...prev,
      openTimeData: {
        ...prev.openTimeData,
        [type]: {
          ...prev.openTimeData[type],
          ...updatedValue,
        },
      },
    }));
  };

  const handleByDayChange = (weekday) => (updatedValue) => {
    setTimeData((prev) => ({
      ...prev,
      openTimeData: {
        ...prev.openTimeData,
        byDay: prev.openTimeData.byDay.map((item) =>
          item.weekday === weekday ? { ...item, ...updatedValue } : item,
        ),
      },
    }));
  };

  // 영업시간 입력 렌더링
  const renderTimeInput = () => {
    const { hourType, hasHoliday } = timeData.openSetting;
    const { openTimeData } = timeData;

    const isAllClosed = openTimeData.byDay.every((item) => item.isOpen === false);
    const isWeekdayClosed = openTimeData.byDay
      .filter((d) => d.weekday >= 1 && d.weekday <= 5)
      .every((d) => d.isOpen === false);
    const isWeekendClosed = openTimeData.byDay
      .filter((d) => d.weekday === 0 || d.weekday === 6)
      .every((d) => d.isOpen === false);

    const getIsClosed = (value) => (hasHoliday ? value : false);

    switch (hourType) {
      case "same":
        return (
          <TimeInput
            name="same"
            label="전체"
            value={timeData.openTimeData.same}
            onChange={handleChange("same")}
            isClosed={getIsClosed(isAllClosed)}
          />
        );

      case "weekday-weekend":
        return (
          <>
            <TimeInput
              name="weekday"
              label="평일"
              value={timeData.openTimeData.weekday}
              onChange={handleChange("weekday")}
              isClosed={getIsClosed(isWeekdayClosed)}
            />
            <TimeInput
              name="weekend"
              label="주말"
              value={timeData.openTimeData.weekend}
              onChange={handleChange("weekend")}
              isClosed={getIsClosed(isWeekendClosed)}
            />
          </>
        );

      case "by-day":
        return timeData.openTimeData.byDay.map((item) => (
          <TimeInput
            key={item.weekday}
            name={`day-${item.weekday}`}
            label={weekdays[item.weekday]}
            value={item}
            onChange={handleByDayChange(item.weekday)}
            isClosed={getIsClosed(item.isOpen === false)}
          />
        ));

      default:
        return null;
    }
  };

  return (
    <SettingContent>
      <Info
        message={`휴무일·영업시간 ${message}`}
        actions={[
          { label: "취소", func: closeForm, key: "cancel" },
          { label: "저장", func: handleSave, key: "add" },
        ]}
      />
      <BizTimeFormContainer>
        <FormContainer>
          <Switch
            label="휴무일 설정"
            keyName="hasHoliday"
            options={{ true: "있음", false: "없음" }}
            value={!!timeData.openSetting.hasHoliday}
            onChange={(value) => changeSwitch("hasHoliday", value)}
          />
          {timeData.openSetting.hasHoliday && (
            <li className="holidaySelect">
              <span className="tit">휴무일 설정</span>
              <div className="cont">
                <SelectOptions
                  weekdays={weekdays}
                  openTimeData={timeData.openTimeData}
                  setOpenTimeData={(updater) =>
                    setTimeData((prev) => ({
                      ...prev,
                      openTimeData:
                        typeof updater === "function" ? updater(prev.openTimeData) : updater,
                    }))
                  }
                />
              </div>
            </li>
          )}
          <Switch
            label="영업시간 설정"
            keyName="hourType"
            options={{
              same: "모든 영업일 동일",
              "weekday-weekend": "평일/주말 구분",
              "by-day": "요일별 구분",
            }}
            value={timeData.openSetting.hourType}
            onChange={(value) => changeSwitch("hourType", value)}
          />
          <li className="timeInputGroup">
            <span className="tit">영업시간 설정</span>
            <div className="cont">{renderTimeInput()}</div>
          </li>
        </FormContainer>
        <CompanyPreview
          company={company}
          activeTab={1}
          selectCompany={selectCompany}
          formData={{
            businessHours: buildSubmitData(timeData.openTimeData, timeData.openSetting.hourType),
          }}
          visibleMode="time"
        />
      </BizTimeFormContainer>
    </SettingContent>
  );
};

export default BizTime;
